import {
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Tooltip
} from "@material-ui/core";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { CardSpacer } from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import { Container } from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import Metadata, { MetadataFormData } from "@saleor/components/Metadata";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import SeoForm from "@saleor/components/SeoForm";
import VisibilityCard from "@saleor/components/VisibilityCard";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import useDateLocalize from "@saleor/hooks/useDateLocalize";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { getChoices } from "@saleor/products/utils/data";
import { CoverageTypeEnum, PageTypeEnum } from "@saleor/types/globalTypes";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import useMetadataChangeTrigger from "@saleor/utils/metadata/useMetadataChangeTrigger";
import { ContentState, convertToRaw, RawDraftContentState } from "draft-js";
import React, { Fragment } from "react";
import { useIntl } from "react-intl";

import CollectionDetails from "../CollectionDetails/CollectionDetails";
import CollectionHeader from "../CollectionHeader";
import { CollectionImage } from "../CollectionImage/CollectionImage";
import CollectionThumbnailImage from "../CollectionThumbnailImage";

export interface CollectionCreatePageFormData extends MetadataFormData {
  backgroundImage: {
    url: string | null;
    value: string | null;
  };
  thumbnailLinkImage: {
    alt: string | null;
    url: string | null;
  };
  backgroundImageAlt: string;
  color: string | null;
  description: RawDraftContentState;
  iconImage: {
    alt: string | null;
    url: string | null;
  };
  name: string;
  slug: string;
  publicationDate: string;
  isPublished: boolean;
  isFeatured: boolean;
  seoDescription: string;
  seoTitle: string;
  featuredIn: PageTypeEnum[];
  isDirectPurchaseEnabled: boolean;
}

export interface CollectionCreatePageProps {
  disabled: boolean;
  errors: ProductErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  onIconToUpload: (file: File) => void;
  onFileSizeError: () => void;
  onBack: () => void;
  onSubmit: (data: CollectionCreatePageFormData) => void;
  onThumbnailLinkImageUpload: (file: File) => void;
  coverageType: CoverageTypeEnum;
}

const initialForm: CollectionCreatePageFormData = {
  backgroundImage: {
    url: null,
    value: null
  },
  backgroundImageAlt: "",
  color: null,
  description: convertToRaw(ContentState.createFromText("")),
  featuredIn: [],
  iconImage: {
    alt: null,
    url: null
  },
  isDirectPurchaseEnabled: true,
  isFeatured: false,
  isPublished: false,
  metadata: [],
  name: "",
  privateMetadata: [],
  publicationDate: "",
  seoDescription: "",
  seoTitle: "",
  slug: "",
  thumbnailLinkImage: {
    alt: null,
    url: null
  }
};

const CollectionCreatePage: React.FC<CollectionCreatePageProps> = ({
  disabled,
  errors,
  saveButtonBarState,
  onIconToUpload,
  onThumbnailLinkImageUpload,
  onFileSizeError,
  onBack,
  onSubmit,
  coverageType
}: CollectionCreatePageProps) => {
  const intl = useIntl();
  const localizeDate = useDateLocalize();
  const {
    makeChangeHandler: makeMetadataChangeHandler
  } = useMetadataChangeTrigger();
  const hideMeta = true;
  const hideSEO = true;
  const label =
    coverageType === CoverageTypeEnum.NATIONWIDE ? "Nationwide" : "Local";
  const pageTitle = "Create " + label + " Collection";

  const featuredInChoices = [
    {
      label: "Food",
      value: PageTypeEnum.FOOD
    },
    {
      label: "Grocery",
      value: PageTypeEnum.GROCERY
    },
    {
      label: "ShopSM",
      value: PageTypeEnum.SM_FULFILLED
    },
    {
      label: "Shops",
      value: PageTypeEnum.SHOPS
    },
    {
      label: "Cinema",
      value: PageTypeEnum.CINEMA
    },
    {
      label: "Discounts",
      value: PageTypeEnum.DISCOUNTS
    }
  ];

  const [selectedFeatured, setSelectedFeatured] = useStateFromProps(
    getChoices([])
  );

  const breadcrumbDirectory = [
    {
      name: "Collections",
      url: "/collections"
    },
    {
      name: pageTitle
    }
  ];

  const transformedInitialForm = {
    ...initialForm,
    isDirectPurchaseEnabled: coverageType === CoverageTypeEnum.NATIONWIDE
  };

  return (
    <Form initial={transformedInitialForm} onSubmit={onSubmit}>
      {({ change, data, hasChanged, submit, toggleValue }) => {
        const changeMetadata = makeMetadataChangeHandler(change);

        const handleSelectFeatured = createMultiAutocompleteSelectHandler(
          toggleValue,
          setSelectedFeatured,
          selectedFeatured,
          featuredInChoices
        );

        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader title={pageTitle} />
            </div>
            <Grid>
              <div>
                <CollectionDetails
                  data={data}
                  disabled={disabled}
                  errors={errors}
                  onChange={change}
                  featuredInDisplayValue={selectedFeatured}
                  featuredChoices={featuredInChoices}
                  setSelectedFeatured={setSelectedFeatured}
                  onFeaturedChange={handleSelectFeatured}
                />
                <CardSpacer />
                <CollectionImage
                  image={
                    data.backgroundImage.url
                      ? {
                          __typename: "Image",
                          alt: data.backgroundImageAlt,
                          url: data.backgroundImage.url
                        }
                      : null
                  }
                  onImageDelete={() =>
                    change({
                      target: {
                        name: "backgroundImage",
                        value: {
                          url: null,
                          value: null
                        }
                      }
                    } as any)
                  }
                  onImageUpload={file =>
                    change({
                      target: {
                        name: "backgroundImage",
                        value: {
                          url: URL.createObjectURL(file),
                          value: file
                        }
                      }
                    } as any)
                  }
                  onFileSizeError={onFileSizeError}
                  onChange={change}
                  data={data}
                />
                <CollectionHeader
                  data={data}
                  onChange={change}
                  onIconUpload={file => onIconToUpload(file)}
                />
                {!hideSEO ? (
                  <Fragment>
                    <CardSpacer />
                    <SeoForm
                      allowEmptySlug={true}
                      description={data.seoDescription}
                      disabled={disabled}
                      descriptionPlaceholder=""
                      helperText={intl.formatMessage({
                        defaultMessage:
                          "Add search engine title and description to make this collection easier to find"
                      })}
                      slug={data.slug}
                      slugPlaceholder={data.name}
                      title={data.seoTitle}
                      titlePlaceholder={data.name}
                      onChange={change}
                    />
                  </Fragment>
                ) : (
                  ""
                )}
                {!hideMeta ? (
                  <Fragment>
                    <CardSpacer />
                    <Metadata data={data} onChange={changeMetadata} />
                  </Fragment>
                ) : (
                  ""
                )}
              </div>
              <div>
                <VisibilityCard
                  data={data}
                  errors={errors}
                  disabled={disabled}
                  messages={{
                    hiddenLabel: intl.formatMessage({
                      defaultMessage: "Unpublished",
                      description: "collection label"
                    }),
                    hiddenSecondLabel: intl.formatMessage(
                      {
                        defaultMessage: "will be published on {date}",
                        description: "collection"
                      },
                      {
                        date: localizeDate(data.publicationDate, "L")
                      }
                    ),
                    visibleLabel: intl.formatMessage({
                      defaultMessage: "Published",
                      description: "collection label"
                    })
                  }}
                  onChange={change}
                />
                <CardSpacer />
                {coverageType === CoverageTypeEnum.NATIONWIDE && (
                  <Card>
                    <CardTitle
                      title={intl.formatMessage({
                        defaultMessage: "Purchase Setup",
                        description: "Purchase Setup"
                      })}
                    />
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="isDirectPurchaseEnabledCheckBox"
                            color="primary"
                            name="isDirectPurchaseEnabled"
                            checked={data.isDirectPurchaseEnabled}
                            onChange={e => {
                              change({
                                target: {
                                  name: e.target.name,
                                  value: e.target.checked
                                }
                              });
                            }}
                          />
                        }
                        label={
                          <span
                            style={{
                              alignItems: "center",
                              display: "flex",
                              gap: 6
                            }}
                          >
                            Direct Purchase
                            <Tooltip title="Product Collection with this rule will only allow ‘Buy Now’ purchases for all assigned products.">
                              <InfoOutlined fontSize="small" />
                            </Tooltip>
                          </span>
                        }
                        labelPlacement="end"
                      />
                    </CardContent>
                  </Card>
                )}
                <CollectionThumbnailImage
                  data={data}
                  disabled={false}
                  onChange={change}
                  errors={errors}
                  onThumbnailLinkImageUpload={onThumbnailLinkImageUpload}
                />
              </div>
            </Grid>
            <SaveButtonBar
              state={saveButtonBarState}
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onSave={submit}
            />
          </Container>
        );
      }}
    </Form>
  );
};
CollectionCreatePage.displayName = "CollectionCreatePage";
export default CollectionCreatePage;
